import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  effect,
  input,
  viewChild,
} from '@angular/core';
import { FieldTree, FormField } from '@angular/forms/signals';
import { scrollElementIntoView } from '../../../../utils/scroll-into-view.utils';

@Component({
  selector: 'app-textarea',
  imports: [FormField],
  templateUrl: './textarea.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Keeps a caller's static `id` attribute off the host element, which would
  // otherwise duplicate the `id` the template below puts on the real `<textarea>`.
  host: { '[attr.id]': 'null' },
})
export class TextareaComponent {
  readonly field = input.required<FieldTree<string>>();
  readonly id = input('');
  readonly label = input('');
  readonly placeholder = input('');
  readonly rows = input(5);
  readonly maxLength = input<number | null>(null);
  readonly containerClass = input('');
  readonly textareaClass = input('');

  private readonly textareaRef =
    viewChild<ElementRef<HTMLTextAreaElement>>('textareaRef');

  protected readonly state = computed(() => this.field()());

  protected readonly labelText = computed(() =>
    this.state().required() ? `${this.label()} *` : this.label()
  );

  protected readonly showError = computed(() => {
    const state = this.state();
    return state.touched() && state.errors().length > 0;
  });

  protected readonly errorMessage = computed(
    () => this.state().errors()[0]?.message ?? 'This is a required field'
  );

  constructor() {
    // `[formField]` owns the native `maxlength` attribute, so a template binding to it is
    // NG8022 — this per-instance limit is set on the element directly.
    effect(() => {
      const el = this.textareaRef()?.nativeElement;
      if (!el) {
        return;
      }
      const max = this.maxLength();
      if (max === null) {
        el.removeAttribute('maxlength');
      } else {
        el.setAttribute('maxlength', String(max));
      }
    });
  }

  scrollIntoView(options?: ScrollIntoViewOptions): void {
    const el = this.textareaRef()?.nativeElement;
    if (!el) {
      return;
    }

    scrollElementIntoView(el, options);
  }

  focus(): void {
    this.textareaRef()?.nativeElement.focus();
  }
}
